import { useQuery } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export type IaVendor = {
  id: number;
  name: string;
  code?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  active?: boolean;
};

export type IaPoLine = {
  id: number;
  lineType: string;
  orderedQty: number;
  receivedQty: number;
  unitCostCents?: number | null;
  inventoryItem?: { id: number; upc: string; name?: string | null; uomPackType?: string; size?: string };
};

export type IaPurchaseOrder = {
  id: number;
  poNumber: string;
  status: string;
  orderedAt?: string;
  notes?: string | null;
  vendor?: IaVendor;
  receiveLocation?: { id: number; name?: string | null };
  lines?: IaPoLine[];
};

export async function queryIaVendors(search = ''): Promise<IaVendor[]> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/vendor/query', { search, take: 200 });
  return (res.data as { data?: IaVendor[] }).data ?? [];
}

export async function saveIaVendor(vendor: Partial<IaVendor> & { name: string }): Promise<IaVendor> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/vendor/save', vendor);
  return (res.data as { vendor: IaVendor }).vendor;
}

export async function queryIaPurchaseOrders(
  search = '',
  options?: { includeCancelled?: boolean },
): Promise<IaPurchaseOrder[]> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/purchase-order/query', {
    search,
    take: 500,
    includeCancelled: options?.includeCancelled ?? false,
  });
  return (res.data as { data?: IaPurchaseOrder[] }).data ?? [];
}

export const useIaPurchaseOrdersList = (search = '', includeCancelled = false) => {
  return useQuery({
    queryKey: ['ia-purchase-orders', search, includeCancelled],
    queryFn: () => queryIaPurchaseOrders(search, { includeCancelled }),
    staleTime: 30_000,
  });
};

export async function fetchIaPurchaseOrder(id: number): Promise<IaPurchaseOrder> {
  const res = await httpClient.get(`api/inventory-auditor/purchasing/purchase-order/details?id=${id}`);
  return (res.data as { purchaseOrder: IaPurchaseOrder }).purchaseOrder;
}

export type VendorItemUnitCostHint = {
  unitCostCents: number | null;
  source: 'po_line' | 'receipt_line' | 'catalog' | null;
};

export async function fetchIaLastVendorUnitCost(
  vendorId: number,
  inventoryItemId: number,
): Promise<VendorItemUnitCostHint> {
  const res = await httpClient.get(
    `api/inventory-auditor/purchasing/purchase-order/last-unit-cost?vendorId=${vendorId}&inventoryItemId=${inventoryItemId}`,
  );
  return res.data as VendorItemUnitCostHint;
}

export async function createIaPurchaseOrder(payload: {
  vendorId: number;
  receiveLocationId: number;
  poNumber: string;
  notes?: string;
  lines?: Array<{ inventoryItemId: number; orderedQty: number; unitCostCents?: number; lineType?: string }>;
}): Promise<IaPurchaseOrder> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/purchase-order/create', {
    ...payload,
    lines: payload.lines ?? [],
  });
  return (res.data as { purchaseOrder: IaPurchaseOrder }).purchaseOrder;
}

export async function updateIaPurchaseOrder(payload: {
  id: number;
  vendorId?: number;
  receiveLocationId?: number;
  poNumber?: string;
  notes?: string | null;
}): Promise<IaPurchaseOrder> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/purchase-order/update', payload);
  return (res.data as { purchaseOrder: IaPurchaseOrder }).purchaseOrder;
}

export async function cancelIaPurchaseOrder(id: number): Promise<IaPurchaseOrder> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/purchase-order/cancel', { id });
  return (res.data as { purchaseOrder: IaPurchaseOrder }).purchaseOrder;
}

export async function addIaPurchaseOrderLine(payload: {
  purchaseOrderId: number;
  inventoryItemId: number;
  orderedQty: number;
  unitCostCents?: number;
  lineType?: string;
}): Promise<IaPurchaseOrder> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/purchase-order/line/add', payload);
  return (res.data as { purchaseOrder: IaPurchaseOrder }).purchaseOrder;
}

export async function deleteIaPurchaseOrderLine(lineId: number): Promise<IaPurchaseOrder> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/purchase-order/line/delete', { id: lineId });
  return (res.data as { purchaseOrder: IaPurchaseOrder }).purchaseOrder;
}

export async function submitIaPurchaseOrder(id: number): Promise<IaPurchaseOrder> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/purchase-order/submit', { id });
  return (res.data as { purchaseOrder: IaPurchaseOrder }).purchaseOrder;
}

export async function createAndPostIaReceipt(payload: {
  purchaseOrderId: number;
  receiptNumber: string;
  lines: Array<{ purchaseOrderLineId: number; receivedQty: number; unitCostCents: number }>;
}): Promise<void> {
  const createRes = await httpClient.post('api/inventory-auditor/purchasing/goods-receipt/create', {
    purchaseOrderId: payload.purchaseOrderId,
    receiptNumber: payload.receiptNumber,
    lines: payload.lines,
  });
  const gr = (createRes.data as { goodsReceipt: { id: number } }).goodsReceipt;
  await httpClient.post('api/inventory-auditor/purchasing/goods-receipt/post', { id: gr.id });
}

export type IaInventorySettings = {
  costingMethod: string;
  defaultReceiveLocationId?: number | null;
  buyingAlertsEnabled: boolean;
  alertRecipientUserIds: number[];
  alertRecipients: Array<{ id: number; email: string; firstName?: string; lastName?: string }>;
};

export async function fetchIaInventorySettings(): Promise<IaInventorySettings> {
  const res = await httpClient.get('api/inventory-auditor/purchasing/settings');
  return (res.data as { settings: IaInventorySettings }).settings;
}

export async function updateIaInventorySettings(payload: {
  costingMethod?: string;
  defaultReceiveLocationId?: number | null;
  buyingAlertsEnabled?: boolean;
  alertRecipientUserIds?: number[];
}): Promise<IaInventorySettings> {
  const res = await httpClient.post('api/inventory-auditor/purchasing/settings/update', payload);
  return (res.data as { settings: IaInventorySettings }).settings;
}

export async function searchIaAlertUsers(search: string) {
  const res = await httpClient.post('api/user/query', {
    search,
    take: 50,
    skip: 0,
    active: true,
    orderBy: 'email',
    orderDirection: 'ASC',
  });
  const data = (res.data as { data?: Array<{ id: number; email?: string; firstName?: string; lastName?: string }> })
    .data ?? [];
  return data
    .filter((u) => u.email?.trim())
    .map((u) => ({
      id: u.id,
      email: u.email!,
      firstName: u.firstName,
      lastName: u.lastName,
    }));
}

export async function listIaKitComponents(parentInventoryItemId: number) {
  const res = await httpClient.get(
    `api/inventory-auditor/purchasing/kit/list?parentInventoryItemId=${parentInventoryItemId}`,
  );
  return (res.data as { components: Array<{ componentQty: number; componentInventoryItem: { id: number; upc: string; name?: string | null } }> })
    .components;
}

export async function saveIaKitComponents(
  parentInventoryItemId: number,
  components: Array<{ componentInventoryItemId: number; componentQty: number }>,
): Promise<void> {
  await httpClient.post('api/inventory-auditor/purchasing/kit/save', { parentInventoryItemId, components });
}

export async function queryIaReplenishmentPolicies(locationId?: number) {
  const res = await httpClient.post('api/inventory-auditor/purchasing/replenishment/policy/query', {
    locationId,
    take: 500,
  });
  return (res.data as { data: unknown[] }).data ?? [];
}

export async function saveIaReplenishmentPolicy(payload: Record<string, unknown>) {
  await httpClient.post('api/inventory-auditor/purchasing/replenishment/policy/save', payload);
}

export async function queryIaBuyingSuggestions(opts?: { locationId?: number; triggerOnly?: boolean }) {
  const res = await httpClient.post('api/inventory-auditor/purchasing/replenishment/buying-suggestions', {
    triggerOnly: opts?.triggerOnly ?? false,
    locationId: opts?.locationId,
  });
  return (res.data as { data: unknown[] }).data ?? [];
}
